import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

// --- Types ---

interface PendingTransfer {
  id: number;
  company_id: number;
  company_name: string;
  plan: string;
  reference: string;
  amount: number;
  currency: string;
  created_at: string;
}

interface TransfersResponse {
  data: PendingTransfer[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

// --- Constants ---

const PLAN_LABEL: Record<string, string> = {
  basic: 'Basico',
  professional: 'Profesional',
  enterprise: 'Enterprise',
};

// --- Component ---

export default function TransfersPage(): React.ReactElement {
  const queryClient = useQueryClient();

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Auto-dismiss snackbar
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // Fetch pending transfers
  const { data: transfersData, isLoading } = useQuery({
    queryKey: ['admin-pending-transfers'],
    queryFn: () => api.get<TransfersResponse>('/v1/billing/admin/pending-transfers'),
  });

  const transfers = transfersData?.data ?? [];

  // Verify mutation
  const verifyMutation = useMutation({
    mutationFn: (transferId: number) =>
      api.patch(`/v1/billing/admin/verify-transfer/${transferId}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pending-transfers'] });
      setSnackbar({ open: true, message: 'Transferencia verificada correctamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al verificar la transferencia', severity: 'error' });
    },
  });

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Transferencias pendientes</h1>
        <p className="text-sm text-base-content/60">Verifica las transferencias bancarias de las inmobiliarias</p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-base-100 rounded-lg">
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>Inmobiliaria</th>
                <th>Plan</th>
                <th>Referencia</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {transfers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-base-content/60 py-8">
                    No hay transferencias pendientes de verificacion
                  </td>
                </tr>
              ) : (
                transfers.map((transfer) => (
                  <tr key={transfer.id}>
                    <td className="font-medium">{transfer.company_name}</td>
                    <td>
                      <span className="badge badge-outline badge-sm">
                        {PLAN_LABEL[transfer.plan] || transfer.plan}
                      </span>
                    </td>
                    <td>
                      <code className="text-sm bg-base-200 px-2 py-0.5 rounded">
                        {transfer.reference}
                      </code>
                    </td>
                    <td className="whitespace-nowrap">{formatDate(transfer.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => verifyMutation.mutate(transfer.id)}
                        disabled={verifyMutation.isPending}
                      >
                        {verifyMutation.isPending ? (
                          <span className="loading loading-spinner loading-sm" />
                        ) : (
                          'Verificar'
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Toast */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              &#10005;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
